/**
 * Recorvet 2 — Chart.js v4 Unified Bundle & Theme Integration
 */
import Chart from 'chart.js/auto';

// Expose Chart globally on window for legacy & inline Blade scripts
window.Chart = Chart;

/**
 * Returns color tokens and configurations based on active Dark/Light theme
 */
export function getRecorvetChartTheme() {
    const docTheme = document.documentElement.getAttribute('data-bs-theme') || 
                     document.documentElement.getAttribute('data-theme') || 
                     localStorage.getItem('theme');
    const isDark = docTheme === 'dark';

    const textColor = isDark ? '#cbd5e1' : '#475569';
    const gridColor = isDark ? 'rgba(51, 65, 85, 0.6)' : 'rgba(226, 232, 240, 0.8)';
    const borderColor = isDark ? '#475569' : '#cbd5e1';

    return {
        isDark,
        textColor,
        gridColor,
        borderColor,
        tooltip: {
            backgroundColor: isDark ? '#1e293b' : '#0f172a',
            titleColor: '#ffffff',
            bodyColor: '#ffffff',
            borderColor: isDark ? '#334155' : '#0f172a',
            borderWidth: 1,
            padding: 10,
            cornerRadius: 6
        },
        scales: {
            x: {
                ticks: { color: textColor },
                grid: { color: gridColor },
                border: { color: borderColor }
            },
            y: {
                ticks: { color: textColor },
                grid: { color: gridColor },
                border: { color: borderColor }
            }
        }
    };
}

window.getRecorvetChartTheme = getRecorvetChartTheme;

/**
 * Helper to safely destroy any chart currently attached to a canvas element or ID
 * @param {HTMLCanvasElement|string} canvas - Canvas DOM element or ID selector
 */
export function destroyRecorvetChart(canvas) {
    if (!canvas) return;
    const canvasElement = typeof canvas === 'string' ? document.getElementById(canvas.replace(/^#/, '')) : canvas;
    if (!canvasElement) return;

    const existingChart = Chart.getChart(canvasElement);
    if (existingChart) {
        existingChart.destroy();
    }
}

window.destroyRecorvetChart = destroyRecorvetChart;

/**
 * Apply theme defaults globally to Chart.defaults
 */
function applyGlobalChartDefaults() {
    const theme = getRecorvetChartTheme();
    Chart.defaults.color = theme.textColor;
    Chart.defaults.borderColor = theme.gridColor;
    Chart.defaults.font.family = "'Inter', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif";
    Chart.defaults.font.size = 12;
}

applyGlobalChartDefaults();

/**
 * Reactive listener for themeChanged event
 */
window.addEventListener('themeChanged', function (event) {
    const theme = getRecorvetChartTheme();
    applyGlobalChartDefaults();

    // Iterate through all live Chart instances and update colors
    if (Chart.instances) {
        Object.values(Chart.instances).forEach(function (chart) {
            if (!chart || !chart.options) return;

            // Update legend labels color
            if (chart.options.plugins && chart.options.plugins.legend && chart.options.plugins.legend.labels) {
                chart.options.plugins.legend.labels.color = theme.textColor;
            }

            // Update tooltip colors
            if (chart.options.plugins && chart.options.plugins.tooltip) {
                chart.options.plugins.tooltip.backgroundColor = theme.tooltip.backgroundColor;
                chart.options.plugins.tooltip.borderColor = theme.tooltip.borderColor;
            }

            // Update scales
            if (chart.options.scales) {
                Object.keys(chart.options.scales).forEach(function (scaleKey) {
                    const scale = chart.options.scales[scaleKey];
                    if (scale) {
                        if (scale.ticks) scale.ticks.color = theme.textColor;
                        if (scale.grid) scale.grid.color = theme.gridColor;
                        if (scale.border) scale.border.color = theme.borderColor;
                    }
                });
            }

            chart.update('none'); // Update without full animation for performance
        });
    }
});

export default Chart;
